import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { tokens } from '@/theme/theme';

type Props = {
  value: string;
  onChange: (value: string) => void;
  /** Appelé quand les 6 chiffres sont saisis (saisie ou collage). */
  onComplete?: (value: string) => void;
  length?: number;
  disabled?: boolean;
  error?: boolean;
  autoFocus?: boolean;
  label?: string;
};

/** Saisie d'un code à usage unique, une case par chiffre, collage et retour arrière gérés. */
export default function OtpInput({ value, onChange, onComplete, length = 6, disabled, error, autoFocus = true, label = 'Code' }: Props) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length }, (_, i) => value[i] || '');

  useEffect(() => {
    if (autoFocus) refs.current[Math.min(value.length, length - 1)]?.focus();
    // uniquement au montage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commit = (next: string, focusIndex: number) => {
    const clean = next.replace(/\D/g, '').slice(0, length);
    onChange(clean);
    refs.current[Math.min(focusIndex, length - 1)]?.focus();
    if (clean.length === length) onComplete?.(clean);
  };

  const handleChange = (index: number, raw: string) => {
    const typed = raw.replace(/\D/g, '');
    if (!typed) return;
    // Plusieurs chiffres (autoremplissage du code SMS / gestionnaire de mots de passe)
    const next = (value.slice(0, index) + typed).slice(0, length);
    commit(next, index + typed.length);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[index]) commit(value.slice(0, index) + value.slice(index + 1), index);
      else if (index > 0) commit(value.slice(0, index - 1) + value.slice(index), index - 1);
    } else if (e.key === 'ArrowLeft' && index > 0) {
      refs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      refs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;
    e.preventDefault();
    commit(pasted, pasted.length);
  };

  return (
    <Box component="fieldset" sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
      <Box component="legend" sx={{ fontSize: 13, fontWeight: 500, p: 0, mb: 1 }}>
        {label}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))`, gap: 1 }}>
        {digits.map((digit, index) => (
          <Box
            key={index}
            component="input"
            ref={(el: HTMLInputElement | null) => {
              refs.current[index] = el;
            }}
            value={digit}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(index, e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            aria-label={`Chiffre ${index + 1}`}
            aria-invalid={error || undefined}
            disabled={disabled}
            maxLength={length}
            sx={{
              height: 52,
              width: '100%',
              boxSizing: 'border-box',
              textAlign: 'center',
              fontFamily: '"Geist Mono", ui-monospace, monospace',
              fontSize: 20,
              color: 'text.primary',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: error ? 'error.main' : tokens.lineStrong,
              borderRadius: '10px',
              outline: 'none',
              transition: 'border-color .15s, box-shadow .15s',
              '&:focus': { borderColor: tokens.accentStrong, boxShadow: `0 0 0 3px ${tokens.accentSoft}` },
              '&:disabled': { opacity: 0.6 },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
